'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useModal } from 'components/shared/Modal';
import AppHandoffModal from 'components/pulsomics/AppHandoffModal';
import { openPulsomicsApp, pulsomicsJoinLink } from 'components/pulsomics/deeplinks';
import DitherBody from 'components/pulsomics/nights/DitherBody';
import ButtonV2 from 'components/shared/ButtonV2';

import { scienceLandingAsset } from './asset';
import { useReveal } from './useReveal';
import styles from './PulsomicsBand.module.css';

/* §5 — "The pulse doesn't explain itself. People do." (Figma 900:331).
   A full-width band: a blurred photo tinted blue (#001362 colour-blend) as the
   background, with a frosted GLASS card inset over it. Inside the card: copy +
   CTAs on the left, and the reusable DitherBody figure on the right, recoloured
   to a LIGHT figure on the transparent card (the body dissolves into its four
   ground-truth readings and reassembles). */

/* light figure for a dark section — the dark cells become the pale figure mass,
   the lit cells + sparkle stay blue. bg is unused (transparent). */
const FIGURE_PALETTE = {
  bg: '#12224E',
  dark: '#D9E1FA',
  highlight: '#FFFFFF',
  mid: '#93A8E4',
  glitter: '#8FB0FF',
};

export default function PulsomicsBand() {
  /* same Join as the /pulsomics page: a phone opens the app (falling back to its
     store), desktop gets the QR handoff dialog */
  const t = useTranslations('science');
  const ModalClient = useModal();
  const handleJoin = useCallback(() => {
    if (openPulsomicsApp(pulsomicsJoinLink())) return;
    ModalClient.set(
      <AppHandoffModal
        heading={t('science.landing.pulsomics.joinModal.heading')}
        text={t('science.landing.pulsomics.joinModal.text')}
      />,
    );
    ModalClient.show();
  }, [ModalClient, t]);
  // the copy staggers in, then the figure; the glass card + photo stay put
  const revealRef = useReveal<HTMLDivElement>();
  return (
    <section className={styles.section} aria-labelledby="science-pulsomics-title">
      <div className={styles.band}>
        <div className={styles.bg} aria-hidden="true">
          <span
            className={styles.bgImg}
            style={{
              backgroundImage: `url(${scienceLandingAsset('pulsomics-band-bg.jpg')})`,
            }}
          />
          <span className={styles.bgScrim} />
        </div>

        <div ref={revealRef} className={styles.card}>
          <div className={styles.copy}>
            <p className={styles.eyebrow} data-reveal>
              {t('science.landing.pulsomics.eyebrow')}
            </p>
            <h2 id="science-pulsomics-title" className={styles.heading} data-reveal>
              {t('science.landing.pulsomics.title')}
            </h2>
            {/* the full line on desktop, the short one on phones */}
            <p className={styles.lede} data-reveal>
              <span className={styles.ledeLong}>{t('science.landing.pulsomics.body')}</span>
              <span className={styles.ledeShort}>{t('science.landing.pulsomics.bodyShort')}</span>
            </p>
            <div className={styles.ctas} data-reveal>
              <ButtonV2
                variant="white"
                aria-haspopup="dialog"
                onClick={handleJoin}
                label={t('science.landing.pulsomics.join')}
              />
              <ButtonV2
                href="/pulsomics/"
                variant="glassLight"
                label={t('science.landing.pulsomics.learnMore')}
              />
            </div>
          </div>

          <div className={styles.visual} data-reveal>
            <div className={styles.figure}>
              <DitherBody palette={FIGURE_PALETTE} transparent leaders />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
